import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery.ts";
import { removeEmpty } from "@/utils/array/removeEmpty.ts";
import { Video } from "@/types/prodcatBaseTypes.ts";
import { Experiences } from "@/getServerSideProps/monetate/getExperiences/experiencesSchema.ts";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { StandardProps } from "@/components/componentMap.ts";

export type ProductImages = {
  alternatives: string[];
  primary: string;
};

export enum GalleryObjectType {
  IMAGE = "image",
  VIDEO = "video",
}

export interface GalleryImageObject {
  type: "image";
  title: string;
  imageUrl: string;
  thumbnailUrl: string;
}

export interface GalleryVideoObject extends Omit<GalleryImageObject, "type"> {
  type: "video";
  videoUrl?: string | undefined;
  posterUrl?: string | undefined;
}

export type GalleryObject = GalleryImageObject | GalleryVideoObject;

export const spmSessionStorageName = "spmClosedCount";

export const getGalleryObjects = (
  title: string,
  images: ProductImages,
  videos: Video[],
): { images: GalleryImageObject[]; videos: GalleryVideoObject[] } => {
  const outputImages = [images.primary, ...images.alternatives].map((image) => {
    return {
      type: "image" as const,
      imageUrl: image,
      title,
      thumbnailUrl: formatAmplienceImageURL(`${image}?wid=120&hei=160`),
    };
  });

  const outputVideos = videos.map((video) => {
    const videoThumbnail = video.sources.find(
      (source) => source.key === "thumbnail",
    );

    const videoThumbnailBaseUrl = videoThumbnail ? videoThumbnail.url : "";
    const { baseUrl } = getBaseUrlAndQuery(videoThumbnailBaseUrl);
    const videoTumbnailUrlWithParams = formatAmplienceImageURL(
      ` ${baseUrl}?wid=120&hei=160`,
    );

    return {
      type: "video" as const,
      imageUrl: videoThumbnailBaseUrl,
      thumbnailUrl: videoTumbnailUrlWithParams,
      videoUrl: video.sources.find((source) => source.key === "stream")?.url,
      posterUrl: video.sources.find((source) => source.key === "poster")?.url,
      title,
    };
  });

  return { images: outputImages, videos: outputVideos };
};

export const getSortedGalleryItems = <T>(
  galleryImages: T[],
  galleryVideos: T[],
): T[] => {
  const [firstImage, ...restImages] = galleryImages;
  const [firstVideo, ...restVideos] = galleryVideos;
  return removeEmpty([firstImage, firstVideo, ...restImages, ...restVideos]);
};

export type SocialProofMessagesProperties =
  | {
      appId: string;
      withClose: boolean;
      productId: string;
      brand: string;
      availableToOrder: boolean;
      experiencesRawObject: Experiences;
      showSocialProofMessages: boolean;
      setShowSocialProofMessages: (arg: boolean) => void;
      fadeOutSocialProofMessages: boolean;
      setFadeOutSocialProofMessages: (arg: boolean) => void;
      monetateEnvironment: string;
    }
  | undefined;

type SocialProofMessagesConfig = {
  SocialProofMessages?: { appId?: string; withClose?: boolean };
};

export const hasSocialProofMessages = (
  cfg: StandardProps["configProps"],
): cfg is SocialProofMessagesConfig =>
  !!cfg && typeof cfg === "object" && "SocialProofMessages" in cfg;
